import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DataManagerDialogsObject } from '@databox/ember-integrations/lib/components/DataManagerDialogs/DataManagerDialogs';

import type { Service } from 'webapp-types';

export default class DataManagerService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked dataManagerFunctions?: DataManagerDialogsObject = undefined;

	/**
	 * Ids of the sources (data sources and datasets) whose delete the user has confirmed. The connected table hides
	 * them and their datasets, so the Ember Data record stays untouched (and restorable) if the delete
	 * fails. An id is marked on confirm and cleared on failure; otherwise it stays for the session, as ids
	 * are not reused.
	 */
	@tracked pendingDeletionIds: ReadonlySet<string> = new Set();

	markPendingDeletion(id: string): void {
		this.pendingDeletionIds = new Set(this.pendingDeletionIds).add(id);
	}

	clearPendingDeletion(id: string): void {
		const ids = new Set(this.pendingDeletionIds);
		if (ids.delete(id)) {
			this.pendingDeletionIds = ids;
		}
	}

	/** True for a source pending deletion and for the datasets of a data source pending deletion. */
	isPendingDeletion(source: {
		id?: string | null;
		parentInfo?: { spaceAccessId?: number } | null;
	}): boolean {
		const ids = this.pendingDeletionIds;
		if (!ids.size) {
			return false;
		}
		const parentId = source.parentInfo?.spaceAccessId;
		return (!!source.id && ids.has(source.id)) || (!!parentId && ids.has(String(parentId)));
	}

	get functions(): DataManagerDialogsObject | undefined {
		return this.dataManagerFunctions;
	}

	changeSourceTimezone(
		dataSource: Parameters<
			DataManagerDialogsObject['openDataSourceTimezoneDialog']
		>[0]['dataSource'],
	): void {
		this.functions?.openDataSourceTimezoneDialog({ dataSource }).then(
			({ timezone, applyToDescendants }) => {
				this.mmSourcesService.sourceModels
					.filter(
						(source) =>
							source.numericId === dataSource.id ||
							(applyToDescendants &&
								source.parentInfo?.spaceAccessId === dataSource.id),
					)
					.forEach((source) => source.directPropUpdate({ timezone }));

				this.infolayerService.ok('Data source timezone changed successfully!');
			},
			() => undefined,
		);
	}
}

declare module '@ember/service' {
	interface Registry {
		'data-manager': DataManagerService;
	}
}
